import React from 'react';
import Icon from 'components/AppIcon';

export default function BudgetOverview({ categories, loading }) {
  const items = categories || [];
  const totalSpent = items?.reduce((s, i) => s + (parseFloat(i?.spent) || 0), 0);
  const totalLimit = items?.reduce((s, i) => s + (parseFloat(i?.monthly_limit) || 0), 0);
  const overallPct = totalLimit > 0 ? Math.min((totalSpent / totalLimit) * 100, 100) : 0;

  return (
    <div className="bg-[#0F172A] border border-[rgba(6,182,212,0.1)] rounded-xl p-4 md:p-6">
      <div className="flex items-center justify-between mb-4 md:mb-5">
        <div>
          <h3 className="font-heading font-semibold text-base md:text-lg text-[#F8FAFC]">Budget Overview</h3>
          <p className="text-xs text-[#94A3B8] mt-0.5">
            {new Date()?.toLocaleString('default', { month: 'long', year: 'numeric' })}
          </p>
        </div>
        <div className="text-right">
          <p className="font-data text-lg font-semibold text-[#F8FAFC]">${totalSpent?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
          <p className="text-xs text-[#94A3B8]">of <span className="font-data">${totalLimit?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></p>
        </div>
      </div>
      <div className="mb-5">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs text-[#94A3B8]">Overall Budget Used</span>
          <span className={`font-data text-xs font-semibold ${overallPct >= 100 ? 'text-[#EF4444]' : overallPct >= 80 ? 'text-[#F59E0B]' : 'text-[#10B981]'}`}>
            {overallPct?.toFixed(0)}%
          </span>
        </div>
        <div className="h-2 bg-[#1E3A5F] rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${overallPct}%`,
              background: overallPct >= 100 ? '#EF4444' : overallPct >= 80 ? '#F59E0B' : 'linear-gradient(90deg, #06B6D4, #10B981)'
            }}
          />
        </div>
      </div>
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4]?.map((i) => (
            <div key={i} className="h-8 bg-[#1E3A5F] rounded animate-pulse" />
          ))}
        </div>
      ) : items?.length === 0 ? (
        <p className="text-xs text-[#475569] text-center py-4">No spending categories yet. Complete onboarding to set up your budget.</p>
      ) : (
        <div className="space-y-3">
          {items?.map((item) => {
            const spent = parseFloat(item?.spent) || 0;
            const limit = parseFloat(item?.monthly_limit) || 1;
            const pct = Math.min((spent / limit) * 100, 100);
            const isOver = spent > limit;
            return (
              <div key={item?.id}>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${item?.color || '#06B6D4'}20` }}>
                    <Icon name={item?.icon || 'Tag'} size={12} color={item?.color || '#06B6D4'} />
                  </div>
                  <span className="text-xs text-[#CBD5E1] flex-1 min-w-0 truncate">{item?.name}</span>
                  <span className={`font-data text-xs whitespace-nowrap ${isOver ? 'text-[#EF4444]' : 'text-[#94A3B8]'}`}>
                    ${spent?.toFixed(0)} / ${limit?.toFixed(0)}
                  </span>
                  {isOver && <Icon name="AlertCircle" size={12} color="#EF4444" />}
                </div>
                <div className="h-1.5 bg-[#1E3A5F] rounded-full overflow-hidden ml-8">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${pct}%`, backgroundColor: isOver ? '#EF4444' : (item?.color || '#06B6D4') }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}